import APIService from "./APIService";

/**
 * Service for handling SMS Analysis API operations
 *
 * Expected response format:
 * {
 *   "success": true/false,
 *   "message": "Descriptive message",
 *   "data": { ... }
 * }
 */
const SmsAnalysisAPI = {
  /**
   * Save combined analysis results (phone + text) to database
   * @param {Object} analysisData - Combined analysis data
   * @param {string} analysisData.phoneNumber - Phone number (optional)
   * @param {string} analysisData.textMessage - Text message content (optional)
   * @param {Object} analysisData.phoneAnalysis - Phone analysis results (optional)
   * @param {Object} analysisData.textAnalysis - Text analysis results (optional)
   * @returns {Promise} Promise that resolves with saved analysis result
   */
  saveAnalysis: async (analysisData) => {
    try {
      console.log("[SmsAnalysisAPI] Saving combined analysis:", analysisData);
      const response = await APIService.post(
        "/api/v1/scam/detector/sms/save",
        analysisData,
      );
      console.log("[SmsAnalysisAPI] Save analysis response:", response);
      return response;
    } catch (error) {
      console.error("[SmsAnalysisAPI] Error saving analysis:", error);
      throw error;
    }
  },

  /**
   * Get analysis history for the current user
   * @param {number} page - Page number (optional, default 0)
   * @param {number} size - Page size (optional, default 20)
   * @returns {Promise} Promise that resolves with analysis history
   */
  getAnalysisHistory: async (page = 0, size = 20) => {
    try {
      console.log(
        "[SmsAnalysisAPI] Getting analysis history, page:",
        page,
        "size:",
        size,
      );
      const response = await APIService.get(
        `/api/v1/scam/detector/sms/history?page=${page}&size=${size}`,
      );
      console.log("[SmsAnalysisAPI] Analysis history response:", response);
      return response;
    } catch (error) {
      console.error("[SmsAnalysisAPI] Error getting analysis history:", error);
      throw error;
    }
  },

  /**
   * Get analysis by ID
   * @param {number} analysisId - Analysis ID
   * @returns {Promise} Promise that resolves with analysis details
   */
  getAnalysisById: async (analysisId) => {
    try {
      console.log("[SmsAnalysisAPI] Getting analysis by ID:", analysisId);
      const response = await APIService.get(
        `/api/v1/scam/detector/sms/${analysisId}`,
      );
      console.log("[SmsAnalysisAPI] Analysis by ID response:", response);
      return response;
    } catch (error) {
      console.error("[SmsAnalysisAPI] Error getting analysis by ID:", error);
      throw error;
    }
  },

  /**
   * Delete analysis by ID
   * @param {number} analysisId - Analysis ID
   * @returns {Promise} Promise that resolves when analysis is deleted
   */
  deleteAnalysis: async (analysisId) => {
    try {
      console.log("[SmsAnalysisAPI] Deleting analysis by ID:", analysisId);
      const response = await APIService.delete(
        `/api/v1/scam/detector/sms/${analysisId}`,
      );
      console.log("[SmsAnalysisAPI] Delete analysis response:", response);
      return response;
    } catch (error) {
      console.error("[SmsAnalysisAPI] Error deleting analysis:", error);
      throw error;
    }
  },

  // ========== Forwarded SMS Analysis Endpoints ==========

  /**
   * Search SMS analyses with filters
   * @param {Object} params - Search parameters
   * @param {string} params.userUuid - User UUID (optional, defaults to current user)
   * @param {boolean} params.isScam - Filter by scam status (optional)
   * @param {string} params.startDate - Start date for filtering (optional)
   * @param {string} params.endDate - End date for filtering (optional)
   * @param {string} params.searchTerm - Search term for message content or phone (optional)
   * @param {number} params.minConfidence - Minimum confidence score (optional)
   * @param {boolean} params.highConfidenceOnly - Show only high confidence results (optional)
   * @returns {Promise} Promise that resolves with filtered SMS analyses
   */
  searchSmsAnalyses: async (params = {}) => {
    try {
      console.log(
        "[SmsAnalysisAPI] Searching SMS analyses with params:",
        params,
      );
      const queryParams = new URLSearchParams();

      if (params.isScam !== undefined)
        queryParams.append("isScam", params.isScam);
      if (params.startDate) queryParams.append("startDate", params.startDate);
      if (params.endDate) queryParams.append("endDate", params.endDate);
      if (params.searchTerm)
        queryParams.append("searchTerm", params.searchTerm);
      if (params.minConfidence !== undefined)
        queryParams.append("minConfidence", params.minConfidence);
      if (params.highConfidenceOnly)
        queryParams.append("highConfidenceOnly", params.highConfidenceOnly);

      const queryString = queryParams.toString();
      const url = `/api/v1/scam/analysis/sms/search${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await APIService.get(url);
      console.log("[SmsAnalysisAPI] Search SMS analyses response:", response);
      return response;
    } catch (error) {
      console.error("[SmsAnalysisAPI] Error searching SMS analyses:", error);
      throw error;
    }
  },

  /**
   * Get SMS analysis by ID
   * @param {number} id - Analysis record ID
   * @returns {Promise} Promise that resolves with SMS analysis details
   */
  getSmsAnalysisById: async (id) => {
    try {
      console.log("[SmsAnalysisAPI] Getting SMS analysis by ID:", id);
      const response = await APIService.get(`/api/v1/scam/analysis/sms/${id}`);
      console.log("[SmsAnalysisAPI] SMS analysis by ID response:", response);
      return response;
    } catch (error) {
      console.error(
        "[SmsAnalysisAPI] Error getting SMS analysis by ID:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get SMS analysis by Twilio message SID
   * @param {string} messageSid - Twilio message SID
   * @returns {Promise} Promise that resolves with SMS analysis details
   */
  getSmsAnalysisByMessageSid: async (messageSid) => {
    try {
      console.log(
        "[SmsAnalysisAPI] Getting SMS analysis by message SID:",
        messageSid,
      );
      const response = await APIService.get(
        `/api/v1/scam/analysis/sms/by-message-sid/${messageSid}`,
      );
      console.log(
        "[SmsAnalysisAPI] SMS analysis by message SID response:",
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[SmsAnalysisAPI] Error getting SMS analysis by message SID:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get recent SMS analyses (last 7 days)
   * @returns {Promise} Promise that resolves with recent SMS analyses
   */
  getRecentSmsAnalyses: async () => {
    try {
      console.log("[SmsAnalysisAPI] Getting recent SMS analyses");
      const response = await APIService.get("/api/v1/scam/analysis/sms/recent");
      console.log("[SmsAnalysisAPI] Recent SMS analyses response:", response);
      return response;
    } catch (error) {
      console.error(
        "[SmsAnalysisAPI] Error getting recent SMS analyses:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get SMS analysis statistics
   * @param {number} days - Number of days to include in statistics (default: 30)
   * @returns {Promise} Promise that resolves with SMS analysis statistics
   */
  getSmsAnalysisStatistics: async (days = 30) => {
    try {
      console.log(
        "[SmsAnalysisAPI] Getting SMS analysis statistics for days:",
        days,
      );
      const response = await APIService.get(
        `/api/v1/scam/analysis/sms/statistics?days=${days}`,
      );
      console.log(
        "[SmsAnalysisAPI] SMS analysis statistics response:",
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[SmsAnalysisAPI] Error getting SMS analysis statistics:",
        error,
      );
      throw error;
    }
  },

  /**
   * Delete SMS analysis by ID
   * @param {number} id - Analysis record ID
   * @returns {Promise} Promise that resolves when SMS analysis is deleted
   */
  deleteSmsAnalysis: async (id) => {
    try {
      console.log("[SmsAnalysisAPI] Deleting SMS analysis by ID:", id);
      const response = await APIService.delete(
        `/api/v1/scam/analysis/sms/${id}`,
      );
      console.log("[SmsAnalysisAPI] Delete SMS analysis response:", response);
      return response;
    } catch (error) {
      console.error("[SmsAnalysisAPI] Error deleting SMS analysis:", error);
      throw error;
    }
  },

  /**
   * Re-analyze an existing forwarded SMS with additional user context and overwrite stored analysis.
   * @param {number} id - Analysis record ID
   * @param {string} userContext - Additional context provided by user
   * @returns {Promise} Updated SMS analysis entity
   */
  reanalyzeSmsAnalysis: async (id, userContext) => {
    try {
      return await APIService.post(
        `/api/v1/scam/analysis/sms/${id}/reanalyze`,
        {
          userContext,
        },
      );
    } catch (error) {
      console.error("[SmsAnalysisAPI] Error reanalyzing SMS analysis:", error);
      throw error;
    }
  },
};

export default SmsAnalysisAPI;
